import { clone, uniq } from "ramda";
import { type FC, type ReactElement, useCallback, useEffect, useState } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useDispatch } from "react-redux";
import { DialogComponent, HeaderDivider, HStack, Modal } from "widgets";
import { useOverlay } from "widgets/hooks";

import type { ReduxDispatch } from "@/redux/redux-utils.js";
import { SaveTemplateControlledDialog } from "@/ui-modules/core/grid/SaveTemplateForm.js";

import { type InputTableQuerySpec, type Template } from "../../../graphql.js";
import {
  deleteTemplate as deleteTableTemplate,
  fetchColumnTemplates,
  type ITableColumn,
  saveNewTemplate as saveNewTableTemplate,
  setColumnTemplateAsSelected as setColumnTableTemplateAsSelected,
  updateExistingTemplate as updateExistingTableTemplate,
} from "../../../redux/modules/table-data.js";
import { AsyncSearchWithExprParser } from "../forms/AsyncSearchWithExprParser.js";
import { SelectAllNoneWidget } from "../forms/SelectWidget.js";
import ColumnTemplateManager from "./ColumnTemplateManager.js";
import { DragDropTableColumnItem, isFixedGroup, type ITableColumnItemProps } from "./TableColumnItem.js";

export interface ColumnSelectionDialogProps {
  columns: ITableColumn[];
  columnTemplates: Template[];
  tableSpec: InputTableQuerySpec;
  isLoadingTemplates: boolean;
  columnCount?: number;
  onApply: (columns: ITableColumn[]) => Promise<boolean>;
  onCancel: () => void;
}

export interface IColumnNode {
  readonly id: number;
  readonly groupId: number;
  readonly groupName: string;
  readonly column: ITableColumn;
  readonly isSingleton: boolean;
  readonly isGroup: boolean;
  children: IColumnNode[];
  isExpanded: boolean;
  isFilteredOut: boolean;
}

const ColumnSelectionDialog: FC<ColumnSelectionDialogProps> = (props) => {
  const { overlayProps, state } = useOverlay({
    isOpen: true,
    onOpenChange(isOpen) {
      // important for escape key closing
      if (!isOpen) props.onCancel();
    },
  });

  const [openSaveDialog, setOpenSaveDialog] = useState(false);
  const [columns, setColumns] = useState<IColumnNode[]>([]);
  const [columnTemplates, setColumnTemplates] = useState<Template[]>(props.columnTemplates);
  const [filterTerm, setFilterTerm] = useState<string>("");
  const [submissionError, setSubmissionError] = useState<string | null>(null);

  const dispatch = useDispatch<ReduxDispatch>();

  useEffect(() => {
    setColumns(transformColumns(props.columns));
    void dispatch(fetchColumnTemplates(props.tableSpec)).then(setColumnTemplates);
  }, []);

  const getParentNode = useCallback(
    (node: IColumnNode): IColumnNode | null => {
      for (const col of columns) {
        if (col.isGroup && col !== node && col.groupId === node.groupId) {
          return col;
        }
      }
      return null;
    },
    [columns],
  );

  const canDrop = useCallback(
    (props: ITableColumnItemProps, monitor: any): boolean => {
      const targetNode: IColumnNode = props.node;
      const dragNode: IColumnNode = (monitor.getItem() as ITableColumnItemProps).node;

      // Allow drop only on fixed group parent
      if (targetNode.column) {
        const parent = getParentNode(targetNode);
        if (targetNode.column.fixed) {
          // prevent dragging out of an existing group
          const dragParent = getParentNode(dragNode);
          return parent == null && !dragParent;
        } else if (parent && isFixedGroup(parent)) {
          return false;
        }
      }

      if (targetNode.id === dragNode.id) {
        return true;
      }

      if ((dragNode.isSingleton || dragNode.isGroup) && (targetNode.isGroup || targetNode.isSingleton)) {
        return true;
      }

      if (targetNode.isGroup && targetNode.children.findIndex((c) => c.id === dragNode.id) > 0) {
        return true;
      }

      return !targetNode.isGroup && !dragNode.isGroup && targetNode.groupId === dragNode.groupId;
    },
    [getParentNode],
  );

  const columnNodesToTableColumns = useCallback((): ITableColumn[] => {
    //collect the original columns - inverse of transformColumns(...)

    const cleanedColumns: ITableColumn[] = columns.reduce(
      (collector, node) => collector.concat(node.isGroup ? node.children.map((c) => c.column) : [node.column]),
      [],
    );
    // complete the lightweight, clean cols with the search attributes again
    const completeColumns: ITableColumn[] = cleanedColumns.map((clean) => {
      return {
        ...clean,
        searchAttributes: props.columns.find((complete) => complete.columnId === clean.columnId).searchAttributes,
      };
    });

    return completeColumns;
  }, [columns]);

  const handleApply = useCallback(() => {
    props
      .onApply(columnNodesToTableColumns())
      // the onApply callback takes care of updating the active template anyway
      .then(() => {
        const selectedTemplate = columnTemplates.find((template: Template) => !!template.selected);
        if (selectedTemplate) {
          setColumnTableTemplateAsSelected(selectedTemplate, props.tableSpec);
        }
      })
      .catch((e: Error) => {
        setSubmissionError(e.message);
      });
  }, [columnNodesToTableColumns, columnTemplates, props]);

  const getNodeIndexCoordinates = useCallback(
    (nodeId: number, nodes: IColumnNode[]): { parentIdx: number; childIdx: number; isChild: boolean } => {
      for (let i = 0; i < nodes.length; ++i) {
        if (nodes[i].id === nodeId) {
          return { parentIdx: i, childIdx: 0, isChild: false };
        }
        const idx = nodes[i].children.findIndex((c) => c.id === nodeId);
        if (idx > -1) {
          return { parentIdx: i, childIdx: idx, isChild: true };
        }
      }

      return null;
    },
    [],
  );

  const handleCheckbox = useCallback(
    (event, nodeId: number) => {
      const clonedColumns: IColumnNode[] = clone(columns);
      const newHiddenState = !event.target.checked;
      const nodeCoordinates = getNodeIndexCoordinates(nodeId, clonedColumns);
      if (nodeCoordinates.isChild) {
        clonedColumns[nodeCoordinates.parentIdx].children[nodeCoordinates.childIdx].column.hidden = newHiddenState;
      } else {
        const node = clonedColumns[nodeCoordinates.parentIdx];
        const checkedCount = node.children.reduce((count, n) => count + (n.column.hidden ? 0 : 1), 0);
        const fixedCount = node.children.reduce((count, n) => count + (n.column.fixed ? 0 : 1), 0);
        const newGroupHiddenStatte = fixedCount > 0 ? checkedCount === node.children.length : newHiddenState;
        node.isSingleton
          ? (node.column.hidden = !node.column.hidden)
          : node.children.filter((c) => !c.column.fixed).forEach((c) => (c.column.hidden = newGroupHiddenStatte));
      }
      setColumns(clonedColumns);
    },
    [columns],
  );

  const handleToggle = useCallback(
    (nodeId: number) => {
      const clonedColumns: IColumnNode[] = clone(columns);
      const nodeCoordinates = getNodeIndexCoordinates(nodeId, clonedColumns);
      if (nodeCoordinates.isChild) {
        throw new Error("Assertion error: Unexpected node type 'child' found for toggle.");
      }
      clonedColumns[nodeCoordinates.parentIdx].isExpanded = !clonedColumns[nodeCoordinates.parentIdx].isExpanded;
      setColumns(clonedColumns);
    },
    [columns],
  );

  const clearFilter = useCallback(() => {
    const clonedColumns: IColumnNode[] = clone(columns);
    clonedColumns.forEach((n) => {
      if (n.isSingleton) {
        n.isFilteredOut = false;
      } else {
        let childSelected = false;
        n.children.forEach((c) => {
          c.isFilteredOut = false;
          if (!c.column.hidden) {
            childSelected = true;
          }
        });
        n.isFilteredOut = false;
        n.isExpanded = childSelected;
      }
    });

    setFilterTerm("");
    setColumns(clonedColumns);
  }, [columns]);

  const searchExprChanged = useCallback(
    (filterTerm: string, terms: string[]) => {
      return new Promise<void>((resolve) => {
        if (filterTerm.length === 0) {
          clearFilter();
          resolve();
          return;
        }

        const clonedColumns: IColumnNode[] = clone(columns);
        terms.forEach((term) => {
          clonedColumns.forEach((n) => {
            if (n.isSingleton) {
              n.isFilteredOut = !n.column.name.toLowerCase().includes(term);
            } else {
              let childMatches = false;
              n.children.forEach((c) => {
                c.isFilteredOut = !c.column.name.toLowerCase().includes(term);
                childMatches = childMatches || !c.isFilteredOut;
              });
              if (childMatches || n.groupName.toLowerCase().includes(term)) {
                n.isFilteredOut = false;
                n.isExpanded = true;
              } else {
                n.isExpanded = false;
                n.isFilteredOut = true;
              }
            }
          });
        });

        setFilterTerm(filterTerm);
        setColumns(clonedColumns);
        resolve();
      });
    },
    [clearFilter, columns],
  );

  const handleSelection = useCallback(
    (value: boolean) => {
      const clonedColumns: IColumnNode[] = clone(columns);
      clonedColumns.forEach((n) => {
        if (!n.column || (n.column && !n.column.fixed)) {
          n.isSingleton
            ? (n.column.hidden = value)
            : n.children.filter((c) => !c.column.fixed).forEach((c) => (c.column.hidden = value));
        }
      });
      setColumns(clonedColumns);
    },
    [columns],
  );

  const createElements = () => {
    const elements = columns
      .map((c) =>
        c.isFilteredOut ? null : (
          <DragDropTableColumnItem
            key={`${c.id}-node`}
            node={c}
            canDrop={canDrop}
            drop={drop}
            onCheckboxClick={handleCheckbox}
            onToggleGroup={handleToggle}
          />
        ),
      )
      .filter((x) => !!x);

    const columnCount = props.columnCount || 2;
    const listOfLists: any[] = splitColumnList(elements, columnCount);
    const columnElements: ReactElement[] = [];
    for (let i = 0; i < 2; i++) {
      columnElements.push(
        <div key={`sort-selection-form-col-${i}`} style={{ flex: 1 }}>
          {listOfLists[i]}
        </div>,
      );
    }
    return columnElements;
  };

  const setColumnTemplateAsSelected = useCallback(
    (columnTemplate: Template): void => {
      const columnsFromTemplate: ITableColumn[] = [];
      // create an ordered set of columns corresponding to the template with hidden flag set to false
      columnTemplate.columns?.map((columnFromTemplate: ITableColumn) => {
        const c: ITableColumn = props.columns.find(
          (column: ITableColumn) =>
            column.namespace === columnFromTemplate.namespace && column.columnId === columnFromTemplate.columnId,
        );
        if (c) {
          columnsFromTemplate.push({ ...c, hidden: false });
        }
        // else it is possible that the template refers to a column that does not apply to the current group (e.g. different sample type)
      });

      // add all the columns that are not part of the template with hidden flag set to true
      for (const column of props.columns) {
        if (!columnsFromTemplate.find((c: ITableColumn) => c.field === column.field)) {
          columnsFromTemplate.push({ ...column, hidden: true });
        }
      }

      setColumns(transformColumns(columnsFromTemplate));
      setColumnTemplates((templates) =>
        templates.map((ct: Template) => ({
          ...ct,
          selected: ct.id === columnTemplate.id,
        })),
      );
    },
    [columns, props.columns],
  );

  const checkSelectedColumnsFitSelectedTemplate = useCallback(
    (selectedTemplate: Template): boolean => {
      // This routine compares the columns of the "selected" template with the current column selection.
      // This is required when deciding if a template name should be shown in the template manager dropdown
      // when opening the column selector and working in a multi-tab environment.
      // In this case, we can not trust solely on the "selected" flag" of the template because this flag
      // may have changed in the meantime by actions in the other tab.
      const colsFromTemplate: ITableColumn[] = selectedTemplate && selectedTemplate.columns;
      const currentCols: IColumnNode[] = columns;
      const enabledColumns: ITableColumn[] = [];
      const allColumns: ITableColumn[] = [];
      // Cols must be the same, in the same order.
      // First extract the selected cols from the currentCols, considering the nested structure.
      currentCols.forEach((col: IColumnNode) => {
        if (col.column) {
          if (!col.column.hidden) {
            enabledColumns.push(col.column);
          }
          allColumns.push(col.column);
        }

        if (col.children) {
          col.children.forEach((childCol: IColumnNode) => {
            if (childCol.column) {
              if (!childCol.column.hidden) {
                enabledColumns.push(childCol.column);
              }
              allColumns.push(childCol.column);
            }
          });
        }
      });
      //return false if there are more columns enabled in the current table than there are in the template
      if (!colsFromTemplate || colsFromTemplate.length < enabledColumns.length) {
        return false;
      }
      const availableColsFromTemplate = colsFromTemplate.filter(
        (c) => allColumns.find((cc) => cc.columnId === c.columnId) != null,
      );
      // return false if there exists a column in the template that
      // - is available in the current table AND
      // - is not enabled for the current table
      for (let i = 0; i < availableColsFromTemplate.length; ++i) {
        if (
          !enabledColumns[i] ||
          availableColsFromTemplate[i].namespace !== enabledColumns[i].namespace ||
          availableColsFromTemplate[i].columnId !== enabledColumns[i].columnId
        ) {
          return false;
        }
      }
      return true;
    },
    [columns],
  );

  const deleteTemplate = useCallback(
    (columnTemplate: Template): Promise<boolean> => {
      return dispatch(deleteTableTemplate(columnTemplate, props.tableSpec)).then((done: boolean) => {
        if (done) {
          setColumnTemplates((templates) => templates.filter((ct: Template) => ct.id !== columnTemplate.id));
        }
        return done;
      });
    },
    [props.tableSpec],
  );

  const saveNewTemplate = useCallback(
    (name: string): Promise<Template> => {
      return dispatch(saveNewTableTemplate(name, props.tableSpec, columnNodesToTableColumns())).then(
        (newTemplate: Template) => {
          setColumnTemplates((templates) =>
            templates
              .map((ct: Template) => ({
                ...ct,
                selected: false,
              }))
              .concat({ ...newTemplate, selected: true }),
          );

          return newTemplate;
        },
      );
    },
    [columnNodesToTableColumns, props.tableSpec],
  );

  const updateExistingTemplate = useCallback(
    (columnTemplate: Template): Promise<Template> => {
      return dispatch(updateExistingTableTemplate(columnTemplate, columnNodesToTableColumns(), props.tableSpec)).then(
        (updatedTemplate: Template) => {
          setColumnTemplates((templates) =>
            templates.map((ct: Template) =>
              ct.id === columnTemplate.id
                ? {
                    ...updatedTemplate,
                    selected: true,
                  }
                : { ...ct, selected: false },
            ),
          );

          return updatedTemplate;
        },
      );
    },
    [columnNodesToTableColumns, props.tableSpec],
  );

  const drop = useCallback(
    (props: ITableColumnItemProps, monitor: any) => {
      const dragNode = monitor.getItem().node;
      const targetNode = props.node;

      if (dragNode.id === targetNode.id) {
        return;
      }

      const newState = clone(columns);
      const targetCoordinates = getNodeIndexCoordinates(targetNode.id, columns);
      const dragCoordinates = getNodeIndexCoordinates(dragNode.id, columns);

      if (!targetCoordinates.isChild && !dragCoordinates.isChild) {
        newState.splice(dragCoordinates.parentIdx, 1);
        newState.splice(
          dragCoordinates.parentIdx > targetCoordinates.parentIdx
            ? targetCoordinates.parentIdx + 1
            : targetCoordinates.parentIdx,
          0,
          dragNode,
        );
      } else if (!targetCoordinates.isChild && dragCoordinates.isChild) {
        // swap drag-node with first child node
        const parent = newState[targetCoordinates.parentIdx];
        const children = parent.children.slice(0);
        const insertNode = children.splice(dragCoordinates.childIdx, 1)[0];
        parent.children = [insertNode, ...children];
      } else if (targetCoordinates.isChild && dragCoordinates.isChild) {
        const parent = newState[targetCoordinates.parentIdx];
        const children = parent.children.slice(0);
        const insertNode = children.splice(dragCoordinates.childIdx, 1)[0];
        children.splice(
          dragCoordinates.childIdx > targetCoordinates.childIdx
            ? targetCoordinates.childIdx + 1
            : targetCoordinates.childIdx,
          0,
          insertNode,
        );
        parent.children = children;
      }
      setColumns(newState);
    },
    [columns, getNodeIndexCoordinates],
  );

  return (
    <Modal state={state} {...overlayProps}>
      <DialogComponent
        title="Add or Remove Columns"
        mainButton={{ label: "Apply", onPress: handleApply }}
        errorCallout={submissionError ? { message: submissionError } : undefined}
        closeFn={props.onCancel}
        actions={[
          <AsyncSearchWithExprParser
            onSearchExprChange={searchExprChanged}
            controlledValue={filterTerm}
            key="action-1"
          />,
          <SelectAllNoneWidget
            key="action-2"
            current={-1}
            max={1}
            onSelectAll={(all) => {
              handleSelection(!all);
            }}
          />,
          <HeaderDivider key="action-divider" />,
          <ColumnTemplateManager
            key="action-3"
            columnTemplates={columnTemplates}
            columns={columns}
            isLoading={props.isLoadingTemplates}
            setColumnTemplateAsSelected={setColumnTemplateAsSelected}
            deleteTemplate={deleteTemplate}
            checkSelectedColumnsFitSelectedTemplate={checkSelectedColumnsFitSelectedTemplate}
          />,
        ]}
        secondaryButton={{
          label: "Save as Template",
          onPress: () => {
            setOpenSaveDialog(true);
          },
        }}
        style={{ minWidth: 720 }}
      >
        <DndProvider backend={HTML5Backend}>
          <div className="generic-sort-selection-modal">
            <HStack style={{ width: "100%", alignItems: "flex-start" }} spacing={20}>
              {createElements()}
            </HStack>
          </div>
        </DndProvider>
      </DialogComponent>
      {openSaveDialog && (
        <SaveTemplateControlledDialog
          columnTemplates={columnTemplates}
          onClose={() => {
            setOpenSaveDialog(false);
          }}
          saveNewTemplate={saveNewTemplate}
          updateExistingTemplate={updateExistingTemplate}
        />
      )}
    </Modal>
  );
};

function getColumnLabel(col: ITableColumn | null, withGroup?: boolean): string | null {
  if (col == null) {
    return null;
  }

  return withGroup && col.groupName ? `${col.groupName} ${col.name}` : col.name;
}

function splitColumnList(items: ReactElement[], columnNumber: number): ReactElement[][] {
  const minRowsPerCol = 3;
  let rowsPerCol: number;

  // const totalRows = columns.reduce((count, x) => (x.isFilteredOut ? 0 : 1));

  if (items.length <= minRowsPerCol * columnNumber) {
    rowsPerCol = minRowsPerCol;
  } else {
    rowsPerCol = Math.floor((items.length - 1) / columnNumber) + 1;
  }

  const listOfLists: ReactElement[][] = [];
  let myList: ReactElement[] = [];
  let cnt = 0;

  for (let i = 0; i < items.length; ++i) {
    if (i === 0 || cnt === rowsPerCol) {
      if (i !== 0) {
        listOfLists.push(myList);
      }
      myList = [];
      cnt = 0;
    }
    myList.push(items[i]);
    ++cnt;
  }

  listOfLists.push(myList);

  return listOfLists;
}

function transformColumns(columns: ITableColumn[]): IColumnNode[] {
  // SEL9-1506: searchAttributes can contain a lot of vocabulary term data, are not required in this dialog, but badly interfere with the rendering cycles (clone)
  const cleanedColumns: ITableColumn[] = columns.map((c) => {
    return { ...c, searchAttributes: null };
  });

  // Order columns by visible first and then hidden columns sorted by their label (group name followed by name)
  const fixedColumns: ITableColumn[] = cleanedColumns.filter((c: ITableColumn) => !c.hidden && !!c.fixed);
  const visibleColumns: ITableColumn[] = cleanedColumns.filter((c: ITableColumn) => !c.hidden && !c.fixed);
  let hiddenColumns: ITableColumn[] = cleanedColumns
    .filter((c: ITableColumn) => c.hidden)
    .sort((a: ITableColumn, b: ITableColumn) => {
      return getColumnLabel(a, true).localeCompare(getColumnLabel(b, true));
    });

  const allVisibleColumns: ITableColumn[] = fixedColumns.concat(visibleColumns);
  const allVisibleGroupNames: string[] = uniq(allVisibleColumns.filter((c) => !!c.groupName).map((c) => c.groupName));

  // for all groups with at least one visible column
  // move the hidden columns to the first group with the same groupName
  const processedGroupNames = {};
  for (let i = 0; i < allVisibleColumns.length; i++) {
    const g1 = allVisibleColumns[i].groupName;
    const g2 = i < allVisibleColumns.length - 1 ? allVisibleColumns[i + 1].groupName : null;
    if (!!g1 && g1 !== g2 && processedGroupNames[g1] !== 1) {
      processedGroupNames[g1] = 1;
      const toInsert = hiddenColumns.filter((c) => !!c.groupName && c.groupName === g1);
      if (toInsert.length > 0) {
        allVisibleColumns.splice(i + 1, 0, ...toInsert);
        i += toInsert.length;
      }
    }
  }
  hiddenColumns = hiddenColumns.filter((c) => !c.groupName || allVisibleGroupNames.lastIndexOf(c.groupName) < 0);

  const allColumns: ITableColumn[] = allVisibleColumns.concat(hiddenColumns);
  const ret: IColumnNode[] = [];

  let groupNode = null;
  let groupCounter = 0;
  let nodeCounter = 0;
  allColumns.forEach((c) => {
    if (c.groupName) {
      if (groupNode) {
        if (groupNode.groupName === c.groupName) {
          const node: IColumnNode = {
            id: nodeCounter++,
            groupId: groupCounter - 1,
            groupName: null,
            column: c,
            children: [],
            isGroup: false,
            isSingleton: false,
            isExpanded: false,
            isFilteredOut: false,
          };
          groupNode.children.push(node);
          groupNode.isExpanded = groupNode.isExpanded || !c.hidden;
        } else {
          ret.push(groupNode);
          const node: IColumnNode = {
            id: nodeCounter++,
            groupId: groupCounter,
            groupName: null,
            column: c,
            children: [],
            isGroup: false,
            isSingleton: false,
            isExpanded: false,
            isFilteredOut: false,
          };
          groupNode = {
            id: nodeCounter++,
            groupId: groupCounter++,
            groupName: c.groupName,
            column: null,
            children: [node],
            isGroup: true,
            isSingleton: false,
            isExpanded: !c.hidden,
            isFilteredOut: false,
          };
        }
      } else {
        const node: IColumnNode = {
          id: nodeCounter++,
          groupId: groupCounter,
          groupName: null,
          column: c,
          children: [],
          isGroup: false,
          isSingleton: false,
          isExpanded: false,
          isFilteredOut: false,
        };
        groupNode = {
          id: nodeCounter++,
          groupId: groupCounter++,
          groupName: c.groupName,
          column: null,
          children: [node],
          isGroup: true,
          isSingleton: false,
          isExpanded: !c.hidden,
          isFilteredOut: false,
        };
      }
    } else {
      if (groupNode != null) {
        ret.push(groupNode);
        groupNode = null;
      }
      const node: IColumnNode = {
        id: nodeCounter++,
        groupId: groupCounter++,
        groupName: null,
        column: c,
        children: [],
        isGroup: false,
        isSingleton: true,
        isExpanded: false,
        isFilteredOut: false,
      };
      ret.push(node);
    }
  });
  if (groupNode) {
    ret.push(groupNode);
  }
  return ret;
}

export default ColumnSelectionDialog;
